import type { ChannelSlice } from '../ChannelSlice';
import type { ResultsAreaSlice } from '../ResultsAreaSlice';
import type { SlowIdeasSelector } from '../SlowIdeasSelector';
import type { StickyFilterSlice } from '../StickyFilterSlice';
import type { StoreAdapter } from '../StoreAdapter';
import { makeHeaderElements } from '../WidgetModel';
import { COMMON_MENU_HEADER_ELEMENTS } from '../WidgetModel/impl';
import type { WidgetTabSlice } from '../WidgetTabSlice';
import { computed, signal } from '@preact/signals-react';
import type { ReadonlySignal, Signal } from '@preact/signals-react';
import { doesAccessGrantAllowRequest } from '@thinkalpha/common/util/permissions.js';
import type { ConcreteIdea, Idea, WatchlistPlan } from '@thinkalpha/platform-ws-client/contracts/ideas/index.js';
import type { ColumnOptions } from '@thinkalpha/platform-ws-client/contracts/table.js';
import type { ColumnTemplate, KnownColumnTemplateKey } from '@thinkalpha/ui-types/contracts/column-template';
import { union } from 'es-toolkit';
import { t } from 'i18next';
import { optional } from 'inversify';
import type { TableStatus } from 'src/components/table-view/model';
import { IdeaDTO } from 'src/dtos/Idea';
import type { IdeaDescriptor, IdeaPlanDTO } from 'src/dtos/Idea';
import type { GridSettingsNavigationTarget } from 'src/features/grid-settings/models/NavigationModel';
import { ReactiveInjectable, reacts, inject, injectable } from 'src/features/ioc';
import type { InstrumentType } from 'src/features/symbol-picker/state';
import { getValidationColumnDisplayName } from 'src/features/table/lib/validationColumns';
import type { ClientSortModel } from 'src/features/table/types';
import { WATCHLIST_CHANGE_EVENT, WatchlistPlanDTO } from 'src/features/watchlist/dtos/WatchlistPlanDTO';
import { useWidgetTabIdForUserViewKey } from 'src/lib/featureFlags/flags/useWidgetTabIdForUserViewKey';
import { exportTableToCSV } from 'src/lib/util/exportTableCSV';
import type { ColumnOptionsSlice } from 'src/models/ColumnOptionsSlice';
import type {
    WatchListWidgetModel,
    WatchListWidgetModelEventBus,
    WatchListConnectionStatusSignalType,
} from 'src/models/WatchListWidgetModel';
import { getColumnTemplateByIdQuery, getDefaultColumnTemplateQuery } from 'src/queries/columnTemplates';
/* eslint-disable-next-line no-restricted-imports -- legacy direct import */
import { createIdea, updateIdea } from 'src/queries/http/ideas';
import { getIdeaByIdQuery, invalidateIdeaRecordsQueries } from 'src/queries/ideas';
import { getWorkspaceConfigQuery } from 'src/queries/workspace';
import { generateWorkableColumnTemplateGridSettings } from 'src/routes/dashboard/widget-container/widget-settings-editor/context';
import type { WorkspaceService } from 'src/services/WorkspaceService';
import { container as staticContainer } from 'src/StaticContainer';
import { updateWidget } from 'src/store/actions/widgetAndChannel';
import { userDoubleClickedSymbolFromTable } from 'src/store/actions/widgets/linking';
import { setWatchlistIdea } from 'src/store/actions/widgets/watchlist';
import type { WatchlistWidgetViewModel } from 'src/store/types';
import type { FetchedEntityType } from 'src/types';
import type { ReactBindings } from 'src/types/bindings';
import { EventBusBuilder, MergedEventBus } from 'src/types/EventBus/EventBus';
import { v4 } from 'uuid';

const WATCHLIST_COLUMN_TEMPLATE_KEY: KnownColumnTemplateKey = 'ticker::watchlist' as const;

@injectable()
export class WatchListWidgetModelImpl extends ReactiveInjectable implements WatchListWidgetModel {
    // Private fields for slices/selectors (must be declared before eventBus which uses them)
    #channelSlice: ChannelSlice;
    #widgetTabSlice: WidgetTabSlice;
    #stickyFilterSlice: StickyFilterSlice;
    #slowIdeasSelector: SlowIdeasSelector;
    #columnOptionsSlice: ColumnOptionsSlice;

    // Private signals for state management
    #selectedColumnTemplate: Signal<ColumnTemplate | string | null> = signal(null);
    #columnTemplateType: Signal<FetchedEntityType> = signal('default');
    #isColumnTemplateLibraryOpen: Signal<boolean> = signal(false);
    #isGridSettingsDialogOpen: Signal<boolean> = signal(false);
    #gridSettingsDialogTarget: Signal<GridSettingsNavigationTarget | null> = signal(null);
    #tableConnectionStatus: ReadonlySignal<WatchListConnectionStatusSignalType> = signal({
        status: 'uninitialized' as TableStatus,
        progress: 0,
        failureCount: 0,
    });
    // Signals for ideas menu save options
    #canSaveIdea: Signal<boolean> = signal(false);
    #isIdeaTemplate: Signal<boolean> = signal(false);
    #isIdeaDirty: Signal<boolean> = signal(false);
    #userViewKey: Signal<string | undefined> = signal(undefined);
    // Signal for widget name (overrides WidgetTabSlice's name to show idea name)
    #widgetName: Signal<string> = signal('');
    #allowedInstrumentTypes: ReadonlySignal<InstrumentType[]> = signal(['equity']);

    headerElements = makeHeaderElements<WatchListWidgetModelEventBus>([
        ...COMMON_MENU_HEADER_ELEMENTS,
        { type: 'symbolPicker', position: 35, priority: 90, resetOnChange: true },
        { type: 'stickyFilter', position: 36, priority: 80, initialExpanded: false },
        { type: 'helpDocument', documentId: 'watchlist-overview', position: 37, priority: 70 },
        {
            type: 'ideasMenuItems',
            position: 2,
            overflowOnly: true,
            items: ['new', 'open', 'save', 'copy', 'delete'],
            ideaType: 'slow',
            ideaPlanTypes: ['watchlist'],
            selectType: 'single',
            showSelected: false,
            allowDelete: true,
        },
        { type: 'columnTemplateMenu', position: 100, priority: 20, columnTemplateKey: WATCHLIST_COLUMN_TEMPLATE_KEY },
        { type: 'columnOptions', position: 101, priority: 30 },
        { type: 'tableRowCount', position: 103, priority: 40 },
        { type: 'connectionStatus', position: 104, priority: 50 },
        { type: 'exportCSVButton', position: 105, priority: 60 },
    ]);

    eventBus!: WatchListWidgetModel['eventBus'];

    constructor(
        @inject('WidgetDataModel') @reacts private widgetData: ReactBindings['WidgetDataModel'],
        @inject('Store') private store: ReactBindings['Store'],
        @inject('ColumnOptionsSlice') columnOptionsSlice: ColumnOptionsSlice,
        @inject('QueryClient') private queryClient: ReactBindings['QueryClient'],
        @inject('ChannelSlice') channelSlice: ChannelSlice,
        @inject('WidgetTabSlice') widgetTabSlice: WidgetTabSlice,
        @inject('StickyFilterSlice') stickyFilterSlice: StickyFilterSlice,
        @inject('SlowIdeasSelector') slowIdeasSelector: SlowIdeasSelector,
        @inject('ResultsAreaSlice') private resultsAreaSlice: ResultsAreaSlice,
        @inject('SuggestionsService') private suggestionsService: ReactBindings['SuggestionsService'],
        @inject('SlugMapService') private slugMapService: ReactBindings['SlugMapService'],
        @inject('WorkspaceService') @optional() workspaceService?: WorkspaceService,
        @inject('TabId') @optional() tabId?: string,
    ) {
        // eslint-disable-next-line prefer-rest-params -- forwarding constructor args
        super(...arguments);

        // Store slices and selectors as private fields (must be assigned before building eventBus)
        this.#channelSlice = channelSlice;
        this.#widgetTabSlice = widgetTabSlice;
        this.#stickyFilterSlice = stickyFilterSlice;
        this.#slowIdeasSelector = slowIdeasSelector;
        this.#columnOptionsSlice = columnOptionsSlice;

        if (tabId && workspaceService) {
            workspaceService.registerWidget(tabId, this);
            this.disposableStack.defer(() => workspaceService.deregisterWidget(tabId));
        }

        this.#plan = this.#createEmptyWatchlistPlan();
        this.#bindEventListeners();

        // Build eventBus in constructor after slices/selectors are available
        this.eventBus = this.#buildEventBus();
    }

    #createEmptyWatchlistPlan(): WatchlistPlanDTO {
        return new WatchlistPlanDTO(
            {
                isDirty: false,
                includeList: [],
            },
            this.slugMapService,
        );
    }

    init(tabId: string) {
        this.widgetData.init(tabId);
        const isIdeaDirty = (this.widget as WatchlistWidgetViewModel).isIdeaDirty ?? false;
        this.plan = new WatchlistPlanDTO(
            { isDirty: isIdeaDirty, includeList: this.widget.idea.plan.includeList },
            this.slugMapService,
        );

        // Initialize slices
        this.#channelSlice.init(tabId);
        this.#widgetTabSlice.init(tabId);
        this.disposableStack.use(this.#channelSlice);
        this.disposableStack.use(this.#widgetTabSlice);

        // Create a lightweight Redux-backed adapter for slice initialization
        // (WatchList doesn't have a full adapter yet)
        type SliceState = {
            filter?: string | null;
            universeId: string | null;
            scrolling: boolean;
            columnTemplate: ColumnTemplate | string | null;
            columnOptions?: Partial<ColumnOptions>;
        };
        const sliceAdapter: StoreAdapter<SliceState> = {
            getState: () => {
                const widget = this.widget as WatchlistWidgetViewModel;
                return {
                    filter: widget.filter,
                    universeId: null, // Watchlist doesn't use universeId directly
                    scrolling: false, // Watchlist doesn't use scrolling
                    columnTemplate: widget.columnTemplate ?? null,
                    columnOptions: widget.columnOptions ?? undefined,
                };
            },
            persist: (partial) => {
                this.store.dispatch(updateWidget({ tabId, widgetUpdates: partial as Record<string, unknown> }));
            },
            subscribe: (cb) => {
                return this.store
                    .subscribeSelector((state) => {
                        const tab = state.tab.byId[tabId];
                        if (!tab) return null;
                        const widget = tab.widget as WatchlistWidgetViewModel;
                        return {
                            filter: widget.filter,
                            universeId: null,
                            scrolling: false,
                            columnTemplate: widget.columnTemplate ?? null,
                            columnOptions: widget.columnOptions ?? undefined,
                        } satisfies SliceState;
                    })
                    .handle((data) => {
                        if (data) cb(data);
                    });
            },
            [Symbol.dispose]() {
                // no-op — lifecycle managed by disposableStack
            },
        };

        this.#stickyFilterSlice.init(sliceAdapter);
        this.disposableStack.use(this.#stickyFilterSlice);

        // Initialize SlowIdeasSelector — Watchlist doesn't use universeId, but needs
        // the selector for the ideas menu UI. Use the shared adapter with onSlowIdeaSelected callback.
        // syncFromUniverseId: false because watchlist manages its slow idea directly via setSlowIdea().
        this.#slowIdeasSelector.init(sliceAdapter, {
            syncFromUniverseId: false,
            onSlowIdeaSelected: async (payload) => {
                // When a slow idea is selected via the selector, use it
                const ideas = payload.ideas.filter((idea): idea is IdeaDTO<WatchlistPlanDTO> => 'idea' in idea);
                if (ideas[0]) {
                    await this.useIdea(ideas[0]);
                }
            },
        });
        // Perform initial sync of editor signals from the loaded plan
        this.#updateIdeaEditorSignals();

        const uniqueMountId = v4();
        const userViewKeyUnsubscribe = this.store
            .subscribeSelector((state) => useWidgetTabIdForUserViewKey.select(state), { leading: true })
            .handle((useTabId) => {
                this.#userViewKey.value = useTabId ? tabId : uniqueMountId;
            });
        this.disposableStack.defer(userViewKeyUnsubscribe);

        // Initialize ResultsAreaSlice — WatchList uses the dedicated 'watchlist' ideaMode for slow ideas
        this.resultsAreaSlice.init(sliceAdapter, {
            columnTemplateKey: WATCHLIST_COLUMN_TEMPLATE_KEY,
            tabId,
            ideaMode: 'watchlist',
            ideaUserViewKeyScope: 'slow',
            validationModeSignal: signal(false),
            userViewKeySignal: this.#userViewKey,
            onUserSelectedSymbol: (symbol) => this.userSelectedSymbol(symbol),
            stickyFilterEventBus: this.#stickyFilterSlice.eventBus,
            columnOptionsEventBus: this.#columnOptionsSlice.eventBus,
            devtoolsName: 'WatchList/results',
        });

        // Perform initial sync of slow idea (must happen after resultsAreaSlice.init)
        this.#updateSlowIdea();

        this.#tableConnectionStatus = computed(() => ({
            status: this.resultsAreaSlice.table.connectionStatus.value,
            progress: this.resultsAreaSlice.table.progress.value,
            failureCount: this.resultsAreaSlice.table.failureCount.value,
        }));

        this.disposableStack.use(this.resultsAreaSlice);

        this.resolveColumnTemplate(this.widget.columnTemplate ?? null);

        this.#bindEventListeners();

        // Initialize ColumnOptionsSlice with shared adapter
        this.#columnOptionsSlice.init(sliceAdapter);
        this.disposableStack.use(this.#columnOptionsSlice);

        // If the widget was created with defaultIdeaStatus: 'loading', kick off async
        // default idea loading (fire-and-forget — init() is sync)
        if ((this.widget as WatchlistWidgetViewModel).defaultIdeaStatus === 'loading') {
            this.#loadDefaultIdea();
        }
    }

    async #loadDefaultIdea() {
        try {
            const config = await this.queryClient.fetchQuery(getWorkspaceConfigQuery());

            // Apply default universe immediately
            this.updateWidgetState({
                universeId: config.defaultUniverseId,
                idea: {
                    ...this.widget.idea,
                    defaultUniverseId: config.defaultUniverseId,
                },
            });

            const defaultWatchlistId = config.defaultWatchlistPlanId;
            if (!defaultWatchlistId) {
                this.updateWidgetState({ defaultIdeaStatus: 'failed' });
                return;
            }

            // Fetch the idea with a timeout
            const loadedIdea = await Promise.race([
                this.queryClient.fetchQuery(
                    getIdeaByIdQuery<ConcreteIdea & { plan: WatchlistPlan }>(defaultWatchlistId),
                ),
                new Promise<never>((_, reject) => setTimeout(() => reject(new Error('Timeout')), 10_000)),
            ]);

            if (!loadedIdea) {
                this.updateWidgetState({ defaultIdeaStatus: 'failed' });
                return;
            }

            const planDto = new WatchlistPlanDTO(
                { isDirty: true, includeList: loadedIdea.plan.includeList },
                this.slugMapService,
            );
            const ideaDto = IdeaDTO.fromIdeaAndPlan(
                {
                    name: 'Unnamed Watchlist',
                    key: 'ticker',
                    description: 'Watchlist',
                    isTemplate: false,
                    defaultUniverseId: config.defaultUniverseId,
                    defaultColumnTemplateId: null,
                },
                planDto,
            ) as IdeaDTO<WatchlistPlanDTO>;

            await this.useIdea(ideaDto);
            this.updateWidgetState({ defaultIdeaStatus: 'loaded', isIdeaDirty: true });
        } catch {
            this.updateWidgetState({ defaultIdeaStatus: 'failed' });
        }
    }

    private async resolveColumnTemplate(incomingColumnTemplate: ColumnTemplate | string | null): Promise<void> {
        const key = WATCHLIST_COLUMN_TEMPLATE_KEY;

        if (incomingColumnTemplate === null) {
            const queryOptions = getDefaultColumnTemplateQuery(key);
            const template = await this.queryClient.fetchQuery(queryOptions);
            const sanitizedTemplate = generateWorkableColumnTemplateGridSettings(template ?? null);
            this.#selectedColumnTemplate.value = sanitizedTemplate;
            this.#columnTemplateType.value = 'default';
        } else if (typeof incomingColumnTemplate === 'string') {
            const queryOptions = getColumnTemplateByIdQuery(incomingColumnTemplate);
            const template = await this.queryClient.fetchQuery(queryOptions);
            this.#selectedColumnTemplate.value = generateWorkableColumnTemplateGridSettings(template ?? null);
            this.#columnTemplateType.value = 'referenced';
        } else {
            this.#selectedColumnTemplate.value = generateWorkableColumnTemplateGridSettings(incomingColumnTemplate);
            this.#columnTemplateType.value = 'concrete';
        }
    }

    // Signals for fast ideas (unused by watchlist, but required by IdeasSignals type for ideasMenu)
    #fastIdeas = signal<IdeaDTO<IdeaPlanDTO>[]>([]);
    #selectedFastIdeas = signal<{ type: 'idea'; id: string }[]>([]);

    // Computed signal that narrows SlowIdeasSelector's wide type to watchlist's narrow type
    #watchlistSelectedSlowIdeas = computed<{ type: 'idea'; id: string }[]>(() => {
        const selected = this.#slowIdeasSelector.eventBus.getSignal('widget::selectedSlowIdeas').value;
        return selected.filter((s): s is { type: 'idea'; id: string } => s.type === 'idea');
    });

    #buildEventBus(): WatchListWidgetModelEventBus {
        // Type assertion needed due to complex MergedEventBus type inference limitations
        // The merged event bus provides all required signals/events but TypeScript cannot perfectly infer the intersection
        return MergedEventBus.from(
            EventBusBuilder.create()
                // ==================== Events ====================
                // Ideas menu save events
                .addEvent('widget::ideaEditorSave', () => {
                    void this.saveIdea();
                })
                .addEvent('widget::ideaEditorSaveAs', ({ name }) => {
                    void this.saveIdeaAs(name);
                })
                .addEvent('widget::ideaEditorCreateNew', () => {
                    void this.useNewIdea();
                })
                // Fast ideas selection (no-op, watchlist uses slow ideas via SlowIdeasSelector)
                .addEvent('widget::onIdeasSelect', () => {
                    // Watchlist only supports slow ideas selection
                })
                // Column template events
                .addEvent('widget::selectColumnTemplate', async (payload) => {
                    await this.resolveColumnTemplate(payload.templateId);
                    this.updateWidgetState({ columnTemplate: payload.templateId });
                })
                .addEvent('widget::setColumnTemplateLibraryOpen', (payload) => {
                    this.#isColumnTemplateLibraryOpen.value = payload.isOpen;
                })
                .addEvent('widget::updateConcreteTemplate', (payload) => {
                    this.updateWidgetState({ columnTemplate: payload.columnTemplate });
                    this.#selectedColumnTemplate.value = generateWorkableColumnTemplateGridSettings(
                        payload.columnTemplate,
                    );
                    this.#columnTemplateType.value = 'concrete';
                })
                .addEvent('widget::onColumnTemplateLibraryOpen', (payload) => {
                    this.#isColumnTemplateLibraryOpen.value = payload.isOpen;
                })
                // Table events
                .addEvent('widget::exportCSV', () => {
                    void this.#exportCSV();
                })
                // ==================== Signals ====================
                // Ideas menu save signals
                .addSignal('widget::ideaEditorCanSave', () => this.#canSaveIdea)
                .addSignal('widget::ideaEditorIsTemplate', () => this.#isIdeaTemplate)
                .addSignal('widget::ideaEditorIsDirty', () => this.#isIdeaDirty)
                .addSignal('widget::ideaEditorUnsavedChangesLabel', () => computed(() => 'Unsaved Idea'))
                // Column template signals
                .addSignal('widget::formattableGridSettings', () =>
                    computed(() => {
                        const columnTemplate = this.#selectedColumnTemplate.value;
                        if (!columnTemplate) {
                            return { settings: null, defaults: null };
                        }
                        const sanitized = generateWorkableColumnTemplateGridSettings(columnTemplate as ColumnTemplate);
                        return { settings: sanitized, defaults: sanitized };
                    }),
                )
                .addSignal('widget::selectedColumnTemplate', () => this.#selectedColumnTemplate)
                .addSignal('widget::isColumnTemplateLibraryOpen', () => this.#isColumnTemplateLibraryOpen)
                .addEvent('widget::openGridSettingsDialog', (payload) => {
                    this.#isGridSettingsDialogOpen.value = payload.isOpen;
                    this.#gridSettingsDialogTarget.value =
                        payload.isOpen && (payload.targetTab !== undefined || payload.targetColumnId !== undefined)
                            ? { tab: payload.targetTab, columnId: payload.targetColumnId }
                            : null;
                })
                .addSignal('widget::isGridSettingsDialogOpen', () => this.#isGridSettingsDialogOpen)
                .addSignal('widget::gridSettingsDialogTarget', () => this.#gridSettingsDialogTarget)
                // Table signals
                .addSignal('widget::tableRowCount', () => this.resultsAreaSlice.table.countSignal)
                .addSignal('widget::connectionStatus', () => this.#tableConnectionStatus)
                // Symbol picker signal - watchlist doesn't track a "current instrument" so always return empty
                .addSignal('widget::instrument', () => computed(() => ''))
                .addSignal('widget::allowedInstrumentTypes', () => this.#allowedInstrumentTypes)
                .addSignal('widget::selectedFastIdeas', () => this.#selectedFastIdeas)
                .addEvent('widget::connectionStatusRefetch', () => {
                    this.resultsAreaSlice.table.refetch();
                })
                .build(),
        )
            .merge(this.#channelSlice.eventBus)
            .merge(this.#widgetTabSlice.eventBus)
            .merge(this.#stickyFilterSlice.eventBus)
            .merge(this.#slowIdeasSelector.eventBus)
            .merge(
                // Override with narrowly-typed versions for watchlist
                EventBusBuilder.create()
                    .addSignal('widget::selectedSlowIdeas', () => this.#watchlistSelectedSlowIdeas)
                    .addEvent('widget::selectSlowIdea', (payload) => {
                        // Forward to SlowIdeasSelector (narrow type is assignable to wide type)
                        this.#slowIdeasSelector.eventBus.emit('widget::selectSlowIdea', payload);
                    })
                    .build(),
            )
            .merge(this.#columnOptionsSlice.eventBus)
            .merge(this.resultsAreaSlice.eventBus)
            .merge(
                // Override ResultsAreaSlice's widget::instrumentChange handler.
                // MergedEventBus uses last-write-wins, so this must come AFTER
                // .merge(resultsAreaSlice.eventBus) to ensure the symbol picker
                // adds symbols to the watchlist (addSymbol) rather than using
                // ResultsAreaSlice's default onUserSelectedSymbol behavior.
                EventBusBuilder.create()
                    .addEvent('widget::instrumentChange', (payload) => {
                        if (payload.instrument) {
                            this.addSymbol(payload.instrument);
                        }
                    })
                    .build(),
            )
            .merge(
                // Override widget::name to show idea name instead of tab name
                EventBusBuilder.create()
                    .addSignal('widget::name', () => this.#widgetName)
                    .build(),
            ) as unknown as WatchListWidgetModelEventBus;
    }

    userSelectedSymbol(symbol: string) {
        this.store.dispatch(userDoubleClickedSymbolFromTable(this.widgetData.tabId, symbol));
    }

    #plan: WatchlistPlanDTO;
    @reacts private set plan(plan) {
        this.#plan = plan;
        this.#bindEventListeners();
    }
    get plan() {
        return this.#plan;
    }

    get idea() {
        return this.widget.idea;
    }

    #onWatchlistChange() {
        this.#updateSlowIdea();
        this.#updateIdeaEditorSignals();

        // Sync dirty plan to Redux so workspace save serializes the latest state
        this.store.dispatch(
            setWatchlistIdea(
                this.widgetData.tabId,
                {
                    ...this.idea,
                    plan: this.#plan.toContract(),
                },
                this.#plan.isDirty,
            ),
        );

        this.rerender();
    }

    #updateIdeaEditorSignals(ideaOverride?: IdeaDescriptor) {
        const idea = ideaOverride ?? this.idea;
        this.#canSaveIdea.value = this.#computeCanSaveIdea(idea);
        this.#isIdeaTemplate.value = idea.isTemplate;
        this.#isIdeaDirty.value = this.#plan.isDirty;
        this.#widgetName.value = idea.name ?? this.#widgetTabSlice.eventBus.getSignal('widget::name').value;
    }

    /**
     * Updates the slow idea on the SlowIdeasSelector and ResultsAreaSlice based on the current plan.
     * This creates an IdeaDTO that wraps the watchlist plan with the current idea's ID,
     * so that the menu can properly filter out the selected idea from lists,
     * and the ResultsAreaSlice uses it to build the table query.
     */
    #updateSlowIdea(ideaOverride?: IdeaDescriptor) {
        const currentIdea = ideaOverride ?? this.idea;
        const slowIdea = new IdeaDTO({
            idea: {
                // Include the actual idea ID so selectedSlowIdeas is set correctly
                // This allows the menu to filter out the currently selected idea
                id: currentIdea.id,
                key: 'ticker',
                name: currentIdea.name ?? 'Watchlist',
                isTemplate: currentIdea.isTemplate,
                description: currentIdea.description ?? null,
                defaultUniverseId: currentIdea.defaultUniverseId ?? null,
                defaultColumnTemplateId: currentIdea.defaultColumnTemplateId ?? null,
            },
            plan: this.#plan,
        });
        this.#slowIdeasSelector.setSlowIdea(slowIdea);
        this.resultsAreaSlice.setWidgetPlanIdea(slowIdea);
    }

    #bindEventListeners() {
        const listener = this.#onWatchlistChange.bind(this);
        this.#plan.changeTarget.addEventListener(WATCHLIST_CHANGE_EVENT, listener);
        this.disposableStack.defer(() => {
            this.#plan.changeTarget.removeEventListener(WATCHLIST_CHANGE_EVENT, listener);
        });
        // Note: do NOT dispatch the initial event here — widgetData.init(tabId) hasn't been called yet.
        // The initial sync happens in init() via #updateSlowIdea().
    }

    addSymbol(symbol: string) {
        const userSubmittedSymbols = symbol
            .toUpperCase()
            .replace(/[\r\n ]/g, ',')
            .split(',')
            .map((x) => x.trim())
            .filter(Boolean);

        let addedCount = 0;
        this.plan.setIncludeList((includeList) => {
            const currentInclusionList = includeList.map((x) => x.toUpperCase()) ?? [];
            const merged = union(currentInclusionList, userSubmittedSymbols);
            addedCount = merged.length - currentInclusionList.length;
            return merged;
        });

        if (addedCount > 0) {
            try {
                staticContainer
                    .getOptional('AnalyticsService')
                    ?.track({ type: 'watchlist_symbol_added', symbolCount: addedCount });
            } catch {
                // Analytics should never break the app
            }
        }
    }

    #computeCanSaveIdea(idea: IdeaDescriptor) {
        if (!this.#plan.isDirty) {
            return false;
        }

        if ('id' in idea && 'permissions' in idea) {
            return doesAccessGrantAllowRequest(
                (idea as ConcreteIdea).permissions.effectiveDirectAccess || undefined,
                'writer',
            );
        }

        return false;
    }

    get canSaveIdea() {
        return this.#computeCanSaveIdea(this.idea);
    }

    removeSymbol(symbol: string) {
        const normalizedSymbol = symbol.toUpperCase();
        let removed = false;
        this.plan.setIncludeList((includeList) => {
            const currentInclusionList = includeList.map((x) => x.toUpperCase()) ?? [];
            const next = currentInclusionList.filter((x) => x !== normalizedSymbol);
            removed = next.length !== currentInclusionList.length;
            return next;
        });

        if (removed) {
            try {
                staticContainer
                    .getOptional('AnalyticsService')
                    ?.track({ type: 'watchlist_symbol_removed', symbol: normalizedSymbol });
            } catch {
                // Analytics should never break the app
            }
        }
    }

    #getIdeaToSave(): Idea {
        return {
            ...this.idea,
            plan: this.plan.toContract(),
        };
    }

    async saveIdea() {
        const canPut = (_idea: Idea): _idea is ConcreteIdea => {
            return Boolean(this.idea.id);
        };

        const ideaToSave = this.#getIdeaToSave();

        if (!canPut(ideaToSave)) {
            throw new Error('Cannot save Watchlist without an id');
        }

        const newIdea = (await updateIdea(ideaToSave)) as ConcreteIdea & {
            plan: WatchlistPlan;
        };

        invalidateIdeaRecordsQueries(this.queryClient);

        this.useIdea(
            IdeaDTO.fromIdeaAndPlan(
                newIdea,
                WatchlistPlanDTO.fromPlan(newIdea.plan, this.slugMapService),
            ) as IdeaDTO<WatchlistPlanDTO>,
        );

        return newIdea;
    }

    async saveIdeaAs(name: string) {
        const ideaToSave = {
            ...this.#getIdeaToSave(),
            name: name,
        };

        if (!ideaToSave.name) {
            throw new Error('Cannot save Watchlist without a name');
        }

        const newIdea = (await createIdea(ideaToSave)) as ConcreteIdea & {
            plan: WatchlistPlan;
        };

        invalidateIdeaRecordsQueries(this.queryClient);
        this.suggestionsService.addRecentlyCreated({ type: newIdea.plan.type, id: newIdea.id });

        this.useIdea(
            IdeaDTO.fromIdeaAndPlan(
                newIdea,
                WatchlistPlanDTO.fromPlan(newIdea.plan, this.slugMapService),
            ) as IdeaDTO<WatchlistPlanDTO>,
        );

        return newIdea;
    }

    get widget() {
        return this.widgetData.widget as WatchlistWidgetViewModel;
    }

    get table() {
        return this.resultsAreaSlice.table;
    }

    async useNewIdea() {
        const idea = IdeaDTO.fromIdeaAndPlan(
            {
                name: 'Unnamed Watchlist',
                key: 'ticker',
                description: 'Watchlist',
                isTemplate: false,
                defaultUniverseId: this.widget.universeId,
                defaultColumnTemplateId: null,
            },
            this.#createEmptyWatchlistPlan(),
        );

        await this.useIdea(idea as IdeaDTO<WatchlistPlanDTO>);
    }

    async useIdea(idea: IdeaDTO<WatchlistPlanDTO>) {
        this.store.dispatch(
            setWatchlistIdea(
                this.widgetData.tabId,
                {
                    ...idea.idea,
                    key: 'ticker',
                    plan: idea.plan.toContract(),
                },
                false,
            ),
        );

        this.plan = idea.plan;
        // Pass the idea descriptor directly rather than reading from Redux (this.idea),
        // because on desktop the store dispatch is async (IPC round-trip) and the
        // Redux state won't have the new idea yet at this point.
        this.#updateSlowIdea(idea.idea);
        this.#updateIdeaEditorSignals(idea.idea);
    }

    updateWidgetState(updates: Partial<WatchlistWidgetViewModel>) {
        this.store.dispatch(
            updateWidget({
                tabId: this.widgetData.tabId,
                widgetUpdates: updates,
            }),
        );
    }

    setClientSortModel(sortModel: ClientSortModel): void {
        this.resultsAreaSlice.eventBus.emit('widget::setClientSortModel', { sortModel });
    }

    async #exportCSV(): Promise<void> {
        const columnTemplate = this.#selectedColumnTemplate.value;
        if (columnTemplate == null) {
            return;
        }

        const validationColumns = this.resultsAreaSlice.eventBus.getSignal('widget::validationColumns').value;

        const columnNames = [
            ...(columnTemplate as ColumnTemplate).columns.map((col) => ({
                id: col.id,
                name: col.name,
            })),
            ...validationColumns.map((column) => ({
                id: column.id,
                name: getValidationColumnDisplayName(column),
            })),
        ].reduce(
            (names, { id, name }) => {
                names[id] = name;
                return names;
            },
            {} as Record<string, string>,
        );

        await exportTableToCSV(this.resultsAreaSlice.table, columnNames, t);
    }
}
